import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Modal, Form, Input, Select, Upload, message, Tag, Space, Row, Col, Card, Typography, Popconfirm, Progress, Drawer, List, Tabs, Radio, Badge, Alert, Statistic } from 'antd';
import { PlusOutlined, UploadOutlined, SearchOutlined, DownloadOutlined, DeleteOutlined, FileProtectOutlined, EditOutlined, CheckCircleFilled, CloseCircleFilled, ClockCircleOutlined, LinkOutlined } from '@ant-design/icons';
import api from '../utils/api';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const API_BASE = process.env.REACT_APP_API_URL || '';

const DOC_TYPES = [
  { key: 'singer_noc', label: 'Singer NOC', short: 'Singer NOC', perSong: true },
  { key: 'studio_noc', label: 'Studio NOC', short: 'Studio NOC', perSong: true },
  { key: 'singer_agreement', label: 'Singer Agreement', short: 'Singer Agr', perSong: false },
  { key: 'studio_agreement', label: 'Studio Agreement', short: 'Studio Agr', perSong: false },
];
const DOC_LABEL = { ...Object.fromEntries(DOC_TYPES.map(d => [d.key, d.label])), other: 'Other' };
const DOC_COLOR = { singer_noc: 'blue', studio_noc: 'geekblue', singer_agreement: 'purple', studio_agreement: 'magenta', other: 'default' };

export default function SongAgreements({ user }) {
  const userRole = user?.role || 'viewer';
  const canEdit = ['admin', 'manager'].includes(userRole);
  const authToken = localStorage.getItem('token');
  const [agreements, setAgreements] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [songOptions, setSongOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editing, setEditing] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [fileList, setFileList] = useState([]);
  const [filters, setFilters] = useState({ search: '', status: '' });
  const [detail, setDetail] = useState(null);
  const [detailVisible, setDetailVisible] = useState(false);
  const [form] = Form.useForm();
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkProgress, setBulkProgress] = useState(0);
  const [activeTab, setActiveTab] = useState('pending');

  // Upload-pending (ticked) songs
  const [pendingSongs, setPendingSongs] = useState([]);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [pendingSearch, setPendingSearch] = useState('');
  const [pendingOnly, setPendingOnly] = useState('made');
  const [pendingSelected, setPendingSelected] = useState([]);

  // Upload-for-songs modal (single song or multi-song album agreement)
  const [uploadFor, setUploadFor] = useState(null); // { songs: [...], docType }
  const [uploadForFiles, setUploadForFiles] = useState([]);
  const [uploadForForm] = Form.useForm();

  // Link selected songs to an already-uploaded agreement (album NOC/agreement covering more songs)
  const [linkFor, setLinkFor] = useState(null); // { songs: [...] }
  const [linkSearch, setLinkSearch] = useState('');
  const [linkOptions, setLinkOptions] = useState([]);
  const [linkAgreementId, setLinkAgreementId] = useState(null);
  const [linking, setLinking] = useState(false);

  useEffect(() => {
    if (!linkFor) return;
    let alive = true;
    api.get('/agreements', { params: { search: linkSearch } })
      .then(res => { if (alive) setLinkOptions((res.data || []).filter(a => a.file_path)); })
      .catch(() => {});
    return () => { alive = false; };
  }, [linkFor, linkSearch]);

  const submitLink = async () => {
    if (!linkAgreementId) { message.warning('Agreement chuno'); return; }
    setLinking(true);
    try {
      const res = await api.post(`/agreements/${linkAgreementId}/link-songs`, { song_ids: linkFor.songs.map(s => s.id) });
      message.success(res.data.message || 'Linked');
      setLinkFor(null); setLinkAgreementId(null); setLinkSearch(''); setPendingSelected([]);
      refreshAll();
    } catch (err) { message.error(err.response?.data?.error || 'Link failed'); }
    setLinking(false);
  };

  useEffect(() => { fetchCompanies(); fetchSongOptions(); }, []);
  useEffect(() => { fetchAgreements(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, activeTab]);

  const fetchPending = useCallback(async () => {
    setPendingLoading(true);
    try {
      const res = await api.get('/agreements/pending-songs', { params: { search: pendingSearch, only: pendingOnly } });
      setPendingSongs(res.data.songs || []);
    } catch (err) { console.error(err); }
    setPendingLoading(false);
  }, [pendingSearch, pendingOnly]);
  useEffect(() => { fetchPending(); }, [fetchPending]);

  const fetchCompanies = async () => {
    try { const res = await api.get('/companies'); setCompanies(res.data); } catch (err) { console.error(err); }
  };

  const fetchSongOptions = async () => {
    try {
      let allSongs = [];
      let page = 1;
      let hasMore = true;
      while (hasMore) {
        const res = await api.get('/songs', { params: { limit: 100, page } });
        const songs = res.data.songs || [];
        allSongs = allSongs.concat(songs);
        hasMore = songs.length === 100;
        page++;
      }
      setSongOptions(allSongs);
    } catch (err) { console.error(err); }
  };

  const fetchAgreements = async () => {
    setLoading(true);
    try {
      const params = { ...filters };
      if (activeTab !== 'pending' && activeTab !== 'all') params.doc_type = activeTab;
      const res = await api.get('/agreements', { params });
      setAgreements(res.data);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const refreshAll = () => { fetchAgreements(); fetchPending(); };

  const openCreate = () => {
    setEditing(null); form.resetFields();
    form.setFieldsValue({ status: 'done', doc_type: (activeTab !== 'pending' && activeTab !== 'all') ? activeTab : 'singer_noc' });
    setFileList([]); setModalVisible(true);
  };

  const openEdit = async (rec) => {
    setEditing(rec);
    setFileList([]);
    try {
      const res = await api.get(`/agreements/${rec.id}`);
      const a = res.data;
      form.setFieldsValue({
        title: a.title, party: a.party, agreement_date: a.agreement_date, status: a.status,
        notes: a.notes, company_id: a.company_id || undefined, doc_type: a.doc_type || 'other',
        song_ids: (a.songs || []).map(s => s.id)
      });
    } catch (err) { message.error('Failed to load agreement'); }
    setModalVisible(true);
  };

  const handleSubmit = async (values) => {
    if (editing) {
      try {
        await api.put(`/agreements/${editing.id}`, { ...values, song_ids: values.song_ids || [] });
        message.success('Agreement updated');
        setModalVisible(false); setEditing(null); form.resetFields();
        refreshAll();
      } catch (err) { message.error(err.response?.data?.error || 'Update failed'); }
      return;
    }
    const fd = new FormData();
    fd.append('title', values.title);
    fd.append('doc_type', values.doc_type || 'other');
    if (values.party) fd.append('party', values.party);
    if (values.agreement_date) fd.append('agreement_date', values.agreement_date);
    fd.append('status', values.status || 'done');
    if (values.notes) fd.append('notes', values.notes);
    if (values.company_id) fd.append('company_id', values.company_id);
    (values.song_ids || []).forEach(id => fd.append('song_ids[]', id));
    if (fileList[0]?.originFileObj) fd.append('file', fileList[0].originFileObj);
    setUploading(true); setUploadProgress(0);
    try {
      await api.post('/agreements', fd, { onUploadProgress: (e) => { if (e.total) setUploadProgress(Math.round((e.loaded * 100) / e.total)); } });
      setUploadProgress(100);
      message.success('Agreement uploaded');
      setModalVisible(false); form.resetFields(); setFileList([]);
      refreshAll();
    } catch (err) { message.error(err.response?.data?.error || 'Upload failed'); }
    setUploading(false);
  };

  const handleDelete = async (id) => {
    try { await api.delete(`/agreements/${id}`); message.success('Deleted'); refreshAll(); }
    catch (err) { message.error('Delete failed'); }
  };

  const openDetail = async (rec) => {
    setDetail(rec); setDetailVisible(true);
    try { const res = await api.get(`/agreements/${rec.id}`); setDetail(res.data); } catch (err) { /* keep */ }
  };

  // Bulk upload by file name (per-song NOC flow) for a given doc type
  const bulkUploadByName = async (fl, docType) => {
    if (!fl.length) return;
    setBulkUploading(true); setBulkProgress(0);
    try {
      const fd = new FormData();
      fd.append('doc_type', docType);
      fl.forEach(f => fd.append('files[]', f.originFileObj || f));
      const res = await api.post('/agreements/bulk', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => { if (e.total) setBulkProgress(Math.round((e.loaded * 100) / e.total)); }
      });
      setBulkProgress(100);
      message.success(res.data.message || 'Bulk upload done');
      refreshAll();
    } catch (err) { message.error(err.response?.data?.error || 'Bulk upload failed'); }
    setBulkUploading(false); setBulkProgress(0);
  };

  // Upload for specific songs (from the pending list): one file -> all selected songs (album agreement),
  // or many files -> matched to the selected songs by file name / order (NOC).
  const openUploadFor = (songs, docType) => {
    setUploadFor({ songs, docType });
    setUploadForFiles([]);
    uploadForForm.setFieldsValue({ doc_type: docType, mode: DOC_TYPES.find(d => d.key === docType)?.perSong ? 'per_song' : 'one_for_all', party: '', agreement_date: '' });
  };

  const submitUploadFor = async () => {
    const v = uploadForForm.getFieldsValue();
    if (!uploadForFiles.length) { message.warning('File chuno'); return; }
    const songs = uploadFor.songs;
    const fd = new FormData();
    fd.append('doc_type', v.doc_type);
    if (v.party) fd.append('party', v.party);
    if (v.agreement_date) fd.append('agreement_date', v.agreement_date);
    if (v.mode === 'one_for_all') {
      songs.forEach(s => fd.append('song_ids[]', s.id));
      uploadForFiles.forEach(f => fd.append('files[]', f.originFileObj || f));
    } else {
      // per_song: match each file to a song by title contained in the file name; fallback to order
      const norm = (x) => String(x || '').toLowerCase().replace(/\.[a-z0-9]+$/, '').replace(/[^a-z0-9\u0900-\u097F]+/g, ' ').trim();
      const remaining = [...songs];
      const pairs = [];
      uploadForFiles.forEach(f => {
        const fn = norm(f.name);
        let idx = remaining.findIndex(s => fn.includes(norm(s.title)) || norm(s.title).includes(fn));
        if (idx < 0) idx = 0;
        const s = remaining.splice(idx, 1)[0];
        pairs.push({ file: f, song: s });
      });
      pairs.forEach(p => {
        fd.append('files[]', p.file.originFileObj || p.file);
        fd.append('song_id_list[]', p.song ? p.song.id : 0);
      });
    }
    setBulkUploading(true); setBulkProgress(0);
    try {
      const res = await api.post('/agreements/bulk', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => { if (e.total) setBulkProgress(Math.round((e.loaded * 100) / e.total)); }
      });
      setBulkProgress(100);
      message.success(res.data.message || 'Uploaded');
      setUploadFor(null); setUploadForFiles([]); setPendingSelected([]);
      refreshAll();
    } catch (err) { message.error(err.response?.data?.error || 'Upload failed'); }
    setBulkUploading(false); setBulkProgress(0);
  };

  const columns = [
    { title: 'Agreement', dataIndex: 'title', key: 'title', render: (t, r) => <Button type="link" style={{ padding: 0, fontWeight: 600 }} icon={<FileProtectOutlined />} onClick={() => openDetail(r)}>{t}</Button> },
    { title: 'Type', dataIndex: 'doc_type', key: 'doc_type', width: 150, render: (v) => <Tag color={DOC_COLOR[v] || 'default'}>{DOC_LABEL[v] || 'Other'}</Tag> },
    { title: 'Party', dataIndex: 'party', key: 'party', render: (v) => v || '-' },
    { title: 'Songs', dataIndex: 'song_count', key: 'song_count', width: 260, render: (n, r) => (
      <div>
        <Tag color="blue">{n} song(s)</Tag>
        {r.song_titles && <div style={{ fontSize: 12, color: '#555', marginTop: 4, maxHeight: 60, overflow: 'auto' }} title={r.song_titles}>{r.song_titles}</div>}
      </div>
    ) },
    { title: 'Date', dataIndex: 'agreement_date', key: 'date', render: (v) => v || '-' },
    { title: 'Status', dataIndex: 'status', key: 'status', render: (s) => <Tag color={s === 'done' ? 'green' : 'orange'}>{s === 'done' ? 'Done' : 'Pending'}</Tag> },
    {
      title: 'File', key: 'file', width: 200, render: (_, r) => r.file_path
        ? <Space size="small">
            <Button size="small" type="primary" icon={<FileProtectOutlined />} onClick={() => window.open(`${API_BASE}/api/agreements/${r.id}/view?token=${authToken}`, '_blank')}>View</Button>
            <Button size="small" icon={<DownloadOutlined />} href={`${API_BASE}/api/agreements/${r.id}/download?token=${authToken}`} target="_blank">Download</Button>
          </Space>
        : <Text type="secondary">No file</Text>
    },
    ...(canEdit ? [{
      title: 'Actions', key: 'actions', width: 100, render: (_, r) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} />
          {userRole === 'admin' && <Popconfirm title="Delete this agreement?" onConfirm={() => handleDelete(r.id)}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>}
        </Space>
      )
    }] : [])
  ];

  const statusIcon = (n) => Number(n) > 0
    ? <CheckCircleFilled style={{ color: '#52c41a' }} />
    : <CloseCircleFilled style={{ color: '#d9d9d9' }} />;

  const pendingColumns = [
    { title: 'Song', dataIndex: 'title', key: 'title', render: (t, r) => <div><Text strong>{t}</Text>{r.singer && <div><Text type="secondary" style={{ fontSize: 12 }}>{r.singer}</Text></div>}</div> },
    { title: 'Studio', dataIndex: 'studio', key: 'studio', width: 130, render: (v) => v || '-' },
    {
      title: 'Status', key: 'st', width: 190, render: (_, r) => r.agreement_status === 'Uploaded'
        ? <Tag color="green">Uploaded</Tag>
        : <Tag color="orange" icon={<ClockCircleOutlined />}>Bana — Upload Pending</Tag>
    },
    ...DOC_TYPES.map(d => ({
      title: d.short, key: d.key, width: 110, align: 'center',
      render: (_, r) => (
        <Space size={4}>
          {statusIcon(r[`has_${d.key}`])}
          {canEdit && Number(r[`has_${d.key}`]) === 0 && (
            <Button size="small" type="link" style={{ padding: 0 }} onClick={() => openUploadFor([r], d.key)}>Upload</Button>
          )}
        </Space>
      )
    })),
    ...(canEdit ? [{
      title: 'Purane se jodo', key: 'link', width: 120, align: 'center',
      render: (_, r) => <Button size="small" icon={<LinkOutlined />} onClick={() => { setLinkFor({ songs: [r] }); setLinkAgreementId(null); setLinkSearch(''); }}>Jodo</Button>
    }] : []),
  ];

  const pendingSelectedSongs = pendingSongs.filter(s => pendingSelected.includes(s.id));
  const pendingCount = pendingSongs.filter(s => s.agreement_status !== 'Uploaded').length;

  const bulkUploadButton = (docType) => (
    <Upload beforeUpload={() => false} multiple showUploadList={false} accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
      onChange={({ fileList: fl }) => bulkUploadByName(fl, docType)}>
      <Button icon={<UploadOutlined />}>Bulk Upload {DOC_LABEL[docType] || ''} (file name = song title)</Button>
    </Upload>
  );

  const agreementsTable = (docType) => (
    <>
      {canEdit && docType && (
        <Alert type="info" showIcon style={{ marginBottom: 12 }}
          message={DOC_TYPES.find(d => d.key === docType)?.perSong
            ? `${DOC_LABEL[docType]}: har gaane ka alag NOC. Bulk upload me file ka naam = gaane ka title; ya "Upload Pending" tab se gaana chun kar upload karo.`
            : `${DOC_LABEL[docType]}: ek agreement 8/16 gaano (album) ka. "Upload Pending" tab me gaane tick karke "Ek Agreement in sab par" dabao, ya neeche Upload Agreement me songs select karo.`}
        />
      )}
      <Card style={{ marginBottom: 16, borderRadius: 8 }}>
        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Input placeholder="Search: agreement/file name, party, ya agreement me shamil kisi bhi gaane ka naam..." prefix={<SearchOutlined />} value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })} allowClear />
          </Col>
          <Col xs={24} sm={6}>
            <Select placeholder="Filter by Status" value={filters.status || undefined} onChange={(v) => setFilters({ ...filters, status: v || '' })} allowClear style={{ width: '100%' }}>
              <Option value="done">Done</Option>
              <Option value="pending">Pending</Option>
            </Select>
          </Col>
          {canEdit && docType && <Col xs={24} sm={6}>{bulkUploadButton(docType)}</Col>}
        </Row>
      </Card>
      <Table dataSource={agreements} columns={columns} rowKey="id" loading={loading} size="middle" scroll={{ x: 1000 }} />
    </>
  );

  const pendingTab = (
    <>
      <Row gutter={16} style={{ marginBottom: 12 }}>
        <Col xs={12} sm={6}><Card size="small" style={{ background: '#fff7e6', borderRadius: 8 }}><Statistic title="Bana — Upload Pending" value={pendingCount} valueStyle={{ color: '#faad14' }} /></Card></Col>
        <Col xs={12} sm={6}><Card size="small" style={{ background: '#f6ffed', borderRadius: 8 }}><Statistic title="Uploaded (is list me)" value={pendingSongs.length - pendingCount} valueStyle={{ color: '#52c41a' }} /></Card></Col>
      </Row>
      <Card style={{ marginBottom: 12, borderRadius: 8 }}>
        <Row gutter={12} align="middle">
          <Col xs={24} sm={10}>
            <Input placeholder="Song / singer search..." prefix={<SearchOutlined />} value={pendingSearch} onChange={(e) => setPendingSearch(e.target.value)} allowClear />
          </Col>
          <Col xs={24} sm={14}>
            <Radio.Group value={pendingOnly} onChange={(e) => setPendingOnly(e.target.value)} optionType="button" buttonStyle="solid" size="small">
              <Radio.Button value="made_pending">Upload Pending</Radio.Button>
              <Radio.Button value="made">Ticked (sab)</Radio.Button>
              <Radio.Button value="uploaded">Uploaded</Radio.Button>
              <Radio.Button value="all">Sab gaane</Radio.Button>
            </Radio.Group>
          </Col>
        </Row>
      </Card>
      <Alert type="info" showIcon style={{ marginBottom: 12 }}
        message="Song Library me jis gaane par 'Agreement bana liya' tick karo, wo yahan aa jata hai. Har gaane ke saamne Singer NOC / Studio NOC / Singer Agreement / Studio Agreement ka Upload button hai. Kai gaane tick karke ek hi agreement (album) sab par laga sakte ho." />
      {canEdit && pendingSelected.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <Space wrap>
            <Text strong>{pendingSelected.length} songs selected</Text>
            <Button type="primary" icon={<UploadOutlined />} onClick={() => openUploadFor(pendingSelectedSongs, 'singer_agreement')}>Ek Singer Agreement in sab par</Button>
            <Button type="primary" icon={<UploadOutlined />} onClick={() => openUploadFor(pendingSelectedSongs, 'studio_agreement')}>Ek Studio Agreement in sab par</Button>
            <Button icon={<UploadOutlined />} onClick={() => openUploadFor(pendingSelectedSongs, 'singer_noc')}>Singer NOC (har gaane ki file)</Button>
            <Button icon={<UploadOutlined />} onClick={() => openUploadFor(pendingSelectedSongs, 'studio_noc')}>Studio NOC (har gaane ki file)</Button>
            <Button icon={<LinkOutlined />} style={{ borderColor: '#722ed1', color: '#722ed1' }} onClick={() => { setLinkFor({ songs: pendingSelectedSongs }); setLinkAgreementId(null); setLinkSearch(''); }}>Pehle se uploaded agreement se jodo</Button>
            <Button onClick={() => setPendingSelected([])}>Clear</Button>
          </Space>
        </div>
      )}
      <Table dataSource={pendingSongs} columns={pendingColumns} rowKey="id" loading={pendingLoading} size="middle" scroll={{ x: 1000 }}
        rowSelection={canEdit ? { selectedRowKeys: pendingSelected, onChange: setPendingSelected, preserveSelectedRowKeys: true } : undefined}
        pagination={{ pageSize: 50, showTotal: (t) => `${t} songs` }} />
    </>
  );

  return (
    <div>
      <Modal open={uploading} footer={null} closable={false} maskClosable={false} title="Uploading..." width={400}>
        <Progress percent={uploadProgress} status={uploadProgress < 100 ? 'active' : 'success'} />
        <Text type="secondary">{uploadProgress < 100 ? `Uploading... ${uploadProgress}%` : 'Processing on server...'}</Text>
      </Modal>

      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col><Title level={3} style={{ margin: 0 }}>Song Agreements</Title></Col>
        <Col>
          <Space wrap>
            {canEdit && <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>Upload Agreement</Button>}
          </Space>
        </Col>
      </Row>

      <Modal open={bulkUploading} footer={null} closable={false} maskClosable={false} title="Uploading Agreements..." width={400}>
        <Progress percent={bulkProgress} status={bulkProgress < 100 ? 'active' : 'success'} />
        <Text type="secondary">Linking to songs...</Text>
      </Modal>

      <Tabs activeKey={activeTab} onChange={(k) => { setActiveTab(k); }} type="card"
        items={[
          { key: 'pending', label: <Badge count={pendingCount} offset={[10, 0]} size="small"><span>Upload Pending (ticked songs)</span></Badge>, children: pendingTab },
          ...DOC_TYPES.map(d => ({ key: d.key, label: d.label, children: agreementsTable(d.key) })),
          { key: 'all', label: 'All Agreements', children: agreementsTable('') },
        ]} />

      {/* Upload for selected songs */}
      <Modal title={uploadFor ? `Upload ${DOC_LABEL[uploadFor.docType]} — ${uploadFor.songs.length} song(s)` : ''} open={!!uploadFor}
        onCancel={() => setUploadFor(null)} onOk={submitUploadFor} okText="Upload" width={640}>
        {uploadFor && (
          <Form form={uploadForForm} layout="vertical">
            <div style={{ maxHeight: 140, overflow: 'auto', marginBottom: 12, padding: 8, background: '#fafafa', borderRadius: 6 }}>
              {uploadFor.songs.map(s => <Tag key={s.id} style={{ marginBottom: 4 }}>{s.title}</Tag>)}
            </div>
            <Row gutter={12}>
              <Col span={12}>
                <Form.Item name="doc_type" label="Document Type">
                  <Select>{DOC_TYPES.map(d => <Option key={d.key} value={d.key}>{d.label}</Option>)}</Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="mode" label="Kaise lagana hai">
                  <Radio.Group>
                    <Radio value="one_for_all">Ek file — in sab gaano par (album agreement)</Radio>
                    <Radio value="per_song">Har gaane ki alag file (file name = song title)</Radio>
                  </Radio.Group>
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={12}>
              <Col span={12}><Form.Item name="party" label="Party / Singer / Studio (optional)"><Input /></Form.Item></Col>
              <Col span={12}><Form.Item name="agreement_date" label="Agreement Date (optional)"><Input type="date" /></Form.Item></Col>
            </Row>
            <Form.Item label="File(s)">
              <Upload beforeUpload={() => false} multiple fileList={uploadForFiles} onChange={({ fileList: fl }) => setUploadForFiles(fl)} accept=".pdf,.jpg,.jpeg,.png,.doc,.docx">
                <Button icon={<UploadOutlined />}>Select File(s)</Button>
              </Upload>
            </Form.Item>
          </Form>
        )}
      </Modal>

      {/* Link songs to an existing agreement */}
      <Modal title={linkFor ? `${linkFor.songs.length} gaane pehle se uploaded agreement se jodo` : ''} open={!!linkFor}
        onCancel={() => setLinkFor(null)} onOk={submitLink} okText="Jodo" okButtonProps={{ loading: linking, disabled: !linkAgreementId }} width={640}>
        {linkFor && (
          <>
            <Alert type="info" showIcon style={{ marginBottom: 12 }}
              message="Jo agreement/NOC file pehle upload ho chuki hai (jaise 16 gaano ke album ki) — use dhundo aur chuno; ye gaane us par lag jayenge, purane links waise hi rahenge." />
            <div style={{ marginBottom: 12 }}><Text type="secondary">Gaane: </Text>{linkFor.songs.map(s => <Tag key={s.id}>{s.title}</Tag>)}</div>
            <Select showSearch filterOption={false} value={linkAgreementId} onSearch={setLinkSearch} onChange={setLinkAgreementId}
              placeholder="Agreement / file / gaane ka naam likho..." style={{ width: '100%' }} notFoundContent="Koi agreement nahi mila"
              options={linkOptions.map(a => ({ value: a.id, label: `${a.title} — ${DOC_LABEL[a.doc_type] || 'Other'} (${a.song_count} song)` }))} />
            {linkAgreementId && (() => { const a = linkOptions.find(x => x.id === linkAgreementId); return a?.song_titles
              ? <div style={{ marginTop: 8, fontSize: 12, color: '#555' }}>Abhi is par: {a.song_titles}</div> : null; })()}
          </>
        )}
      </Modal>

      {/* Create/Edit Modal */}
      <Modal title={editing ? 'Edit Agreement' : 'Upload Agreement'} open={modalVisible} onCancel={() => { setModalVisible(false); setEditing(null); }} onOk={() => form.submit()} width={640}>
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Row gutter={16}>
            <Col span={14}><Form.Item name="title" label="Agreement Title" rules={[{ required: true }]}><Input placeholder="e.g. Singer Royalty Agreement 2026" /></Form.Item></Col>
            <Col span={10}>
              <Form.Item name="doc_type" label="Document Type" initialValue="singer_noc">
                <Select>{DOC_TYPES.map(d => <Option key={d.key} value={d.key}>{d.label}</Option>)}<Option value="other">Other</Option></Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}><Form.Item name="party" label="Party / Person"><Input placeholder="e.g. Artist name" /></Form.Item></Col>
            <Col span={8}><Form.Item name="agreement_date" label="Agreement Date"><Input type="date" /></Form.Item></Col>
            <Col span={8}>
              <Form.Item name="status" label="Status" initialValue="done">
                <Select><Option value="done">Done</Option><Option value="pending">Pending</Option></Select>
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="company_id" label="Company (optional)">
            <Select allowClear placeholder="All">{companies.map(c => <Option key={c.id} value={c.id}>{c.name}</Option>)}</Select>
          </Form.Item>
          <Form.Item name="song_ids" label="Select Songs (tick the songs this agreement covers — album agreement me 8/16 gaane ek saath)">
            <Select mode="multiple" allowClear showSearch optionFilterProp="label" placeholder="Search & select songs..."
              options={songOptions.map(s => ({ value: s.id, label: `${s.title}${s.singer ? ' — ' + s.singer : ''}` }))} />
          </Form.Item>
          {!editing && (
            <Form.Item label="Agreement File (PDF/Image, optional)">
              <Upload beforeUpload={() => false} maxCount={1} fileList={fileList} onChange={({ fileList: fl }) => setFileList(fl)}>
                <Button icon={<UploadOutlined />}>Select File</Button>
              </Upload>
            </Form.Item>
          )}
          <Form.Item name="notes" label="Notes"><TextArea rows={2} /></Form.Item>
          {editing && <Text type="secondary">To replace the file, delete and re-upload the agreement.</Text>}
        </Form>
      </Modal>

      {/* Detail Drawer */}
      <Drawer title={detail?.title || 'Agreement'} open={detailVisible} onClose={() => setDetailVisible(false)} width={460}>
        {detail && (
          <>
            <Space direction="vertical" size={4} style={{ marginBottom: 16, width: '100%' }}>
              <Tag color={DOC_COLOR[detail.doc_type] || 'default'}>{DOC_LABEL[detail.doc_type] || 'Other'}</Tag>
              {detail.party && <Text>Party: <Text strong>{detail.party}</Text></Text>}
              <Space>
                <Tag color={detail.status === 'done' ? 'green' : 'orange'}>{detail.status === 'done' ? 'Done' : 'Pending'}</Tag>
                {detail.agreement_date && <Text type="secondary">{detail.agreement_date}</Text>}
              </Space>
              {detail.notes && <Text type="secondary">{detail.notes}</Text>}
              {detail.file_path && <Button icon={<DownloadOutlined />} href={`${API_BASE}/api/agreements/${detail.id}/download?token=${authToken}`} target="_blank">Download Agreement File</Button>}
              {canEdit && <Button icon={<PlusOutlined />} onClick={() => { setDetailVisible(false); openEdit(detail); }}>Is agreement me gaane jodo / hatao</Button>}
            </Space>
            <Title level={5}>Covered Songs ({detail.songs?.length || 0})</Title>
            <List dataSource={detail.songs || []} locale={{ emptyText: 'No songs linked' }}
              renderItem={(s) => (
                <List.Item>
                  <List.Item.Meta
                    title={s.title}
                    description={<Space wrap size={4}>
                      {s.singer && <Text type="secondary">{s.singer}</Text>}
                      {s.isrc && <Tag>ISRC: {s.isrc}</Tag>}
                      {s.upc && <Tag>UPC: {s.upc}</Tag>}
                      <Tag color={s.distribution_status === 'distributed' ? 'green' : 'orange'}>{s.distribution_status}</Tag>
                    </Space>}
                  />
                </List.Item>
              )} />
          </>
        )}
      </Drawer>
    </div>
  );
}
